import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import {
  requestMagicLink as apiRequestMagicLink,
  verifyMagicLink as apiVerifyMagicLink,
  googleLogin as apiGoogleLogin,
  getMe,
} from "../services/auth";
import { getProfile, applyReferral } from "../services/profiles";
import { onUnauthorized } from "../services/axios";
import { clearTokens, setAccessToken } from "../store/authStore";
import { extractAccessToken, extractUser } from "../utils/tokens";

const AuthContext = createContext(null);

async function buildUserWithProfile(base) {
  if (!base) return null;
  try {
    const p = await getProfile();
    return {
      ...base,
      profileId: p.id,
      name: p.fullName || base.name || "",
      handle: p.username ? `@${p.username}` : base.handle || "",
      avatar: p.avatarUrl || base.avatar || "",
      bio: p.bio || base.bio || "",
      website: p.socialLinks?.website || base.website || "",
      twitter: p.socialLinks?.twitter || base.twitter || "",
      cltHeld: p.cltHeld ?? base.cltHeld ?? 0,
      cisScore: p.cisScore ?? base.cisScore ?? 0,
      streak: p.streak ?? base.streak ?? 0,
      followers: p.followers ?? base.followers ?? 0,
      following: p.following ?? base.following ?? 0,
    };
  } catch {
    return base;
  }
}

function persistAccessToken(data) {
  const accessToken = extractAccessToken(data);
  if (accessToken) setAccessToken(accessToken);
  return accessToken;
}

function isCallbackUrl() {
  const params = new URLSearchParams(window.location.search);
  return (
    params.has("token") ||
    params.has("id") ||
    params.has("access_token") ||
    params.has("code")
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginInProgressRef = useRef(isCallbackUrl());

  useEffect(() => {
    if (loginInProgressRef.current) return;

    const init = async () => {
      try {
        const data = await getMe();
        const base = extractUser(data);
        if (!base) {
          if (!loginInProgressRef.current) setUser(null);
          return;
        }
        setUser(await buildUserWithProfile(base));
      } catch {
        if (!loginInProgressRef.current) {
          setUser(null);
          clearTokens();
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    return onUnauthorized(() => {
      if (!loginInProgressRef.current) {
        setUser(null);
      }
    });
  }, []);

  const requestMagicLink = useCallback(
    (email) => apiRequestMagicLink(email),
    [],
  );

  const completeAuth = useCallback(async (data) => {
    loginInProgressRef.current = true;
    try {
      persistAccessToken(data);
      const base = extractUser(data);
      if (!base) {
        flushSync(() => setUser(null));
        return null;
      }
      flushSync(() => setUser(base));
      const merged = await buildUserWithProfile(base);
      setUser(merged);

      const pendingCode = localStorage.getItem("chicago_pending_referral");
      if (pendingCode) {
        localStorage.removeItem("chicago_pending_referral");
        applyReferral(pendingCode).catch(() => {}); // silent — backend rejects dupes
      }

      return merged;
    } finally {
      loginInProgressRef.current = false;
      setLoading(false);
    }
  }, []);

  const verifyMagicLink = useCallback(
    async (id, token) => {
      const data = await apiVerifyMagicLink(id, token);
      return completeAuth(data);
    },
    [completeAuth],
  );

  const loginWithGoogle = useCallback(
    async (idToken) => {
      const data = await apiGoogleLogin(idToken);
      return completeAuth(data);
    },
    [completeAuth],
  );

  const logout = useCallback(() => {
    setUser(null);
    clearTokens();
  }, []);

  const refreshUser = useCallback(async () => {
    const data = await getMe();
    const base = extractUser(data);
    if (!base) {
      setUser(null);
      return null;
    }
    const merged = await buildUserWithProfile(base);
    setUser(merged);
    return merged;
  }, []);

  const patchUser = useCallback((fields) => {
    setUser((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        requestMagicLink,
        verifyMagicLink,
        loginWithGoogle,
        completeAuth,
        logout,
        refreshUser,
        patchUser,
        isAdmin: user?.role === "admin",
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}